import React from "react";
import { Modal, Pressable, View } from "react-native";
import {
  useSafeAreaFrame,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import { CloseIcon } from "./Buttons";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import { t } from "@/localization";
import tw from "twrnc";

// Centered dialog; with `fullScreenOnMobile` it becomes a full-screen sheet on phones and
// the mobile web layout. `footer` (the call to action) stays pinned under the content,
// which scrolls on its own when it doesn't fit.
const AppModal = ({
  visible,
  onRequestClose,
  children,
  footer,
  fullScreenOnMobile = false,
  maxWidth = 440,
  maxHeight = 550,
  viewStyle,
}) => {
  const { width: responsiveWidth } = useSafeAreaFrame();
  const insets = useSafeAreaInsets();
  const isMobileUX = useIsMobileUX();
  const close = () => onRequestClose?.();
  const closeButton = (
    <Pressable
      onPress={close}
      accessibilityRole="button"
      accessibilityLabel={t("Close")}
      hitSlop={8}
      style={tw`w-8 absolute top-1 right-1 pt-3 z-10`}
    >
      <CloseIcon />
    </Pressable>
  );

  if (fullScreenOnMobile && isMobileUX) {
    return (
      <Modal
        animationType="slide"
        presentationStyle="fullScreen"
        visible={visible}
        onRequestClose={close}
      >
        <View style={[tw`flex-1 bg-white`, { paddingTop: insets.top }]}>
          <View style={tw`h-12`}>{closeButton}</View>
          <View style={[tw`flex-1 px-6`, viewStyle]}>{children}</View>
          {!!footer && (
            <View
              style={[
                tw`px-6 pt-3 border-t border-gray-200 bg-white`,
                { paddingBottom: Math.max(insets.bottom, 16) },
              ]}
            >
              {footer}
            </View>
          )}
        </View>
      </Modal>
    );
  }

  // Pressables rather than Touchables: a Touchable around the content took the touch
  // and made ScrollViews inside the dialog hard to scroll.
  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={visible}
      onRequestClose={close}
    >
      <Pressable
        style={tw`flex-1 justify-center items-center self-center w-[${responsiveWidth}px] bg-[rgba(151,151,151,0.45)]`}
        onPress={close}
      >
        <Pressable
          onPress={() => {}}
          style={[
            tw`flex-1 max-h-[${maxHeight}px] w-[${responsiveWidth}px] max-w-[${maxWidth}px] mx-5 mb-9 md:mb-[-3.5] bg-white rounded-[20px] p-9 shadow-lg`,
            viewStyle,
          ]}
        >
          {closeButton}
          <View style={tw`flex-1`}>{children}</View>
          {!!footer && <View style={tw`pt-4`}>{footer}</View>}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export default AppModal;
